import React, { useCallback, useEffect, useMemo, useState } from "react";
import Popup from "reactjs-popup";
import { PopupProps } from "reactjs-popup/dist/types";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton } from "@mui/material";
import classnames from "classnames";
import { DatePickerStyled } from "components/forms";

import { TooltipDiv } from "../..";
import { CustomComponent } from "../../../store/types";
import {
  ContextMenuOptionFunctionType,
  ContextMenuOptionObjectType,
  ContextMenuOptions,
  ContextMenuOptionType,
  ContextMenuOptionValue,
  IOptionProps,
  OptionsHash
} from "./Options-collection";

import "./ContextMenu.styles.scss";

export type ContextMenuTheme = "standard" | "item-underline";

export type ContextMenuEnabledOptions = ContextMenuOptionType[];

export interface ContextMenuProps extends Partial<PopupProps> {
  isHidden?: boolean;
  triggerStyles?: React.CSSProperties;
  arrowOffsetX?: number;
  className?: string;
  theme?: ContextMenuTheme;
  dataTestid?: string;
  pasteLineAfter?: ContextMenuOptionType | ContextMenuOptionType[];
  options?: ContextMenuOptions;
  tooltipMessage?: string;
  enabledOptions?: ContextMenuEnabledOptions;
  trigger?: JSX.Element;
  onOpen?: () => void;
  onClose?: () => void;
}

export const ContextMenu: React.FC<ContextMenuProps> = ({
  position = "bottom left",
  triggerStyles = {},
  className = "",
  theme = "standard",
  // arrowOffsetX = 22,
  isHidden = false,
  dataTestid = "",
  pasteLineAfter = [],
  options = {},
  trigger = (
    <MoreVertIcon
      sx={{ width: "20px", height: "20px" }}
      data-testid={dataTestid ? dataTestid : "context-menu-trigger"}
      style={triggerStyles}
    />
  ),
  tooltipMessage = "",
  enabledOptions = [],
  onOpen: externalOnOpen,
  onClose: externalOnClose,
  ...props
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [getDateObj, setGetDateObj] = useState<ContextMenuOptionObjectType | null>(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const handleOpen = (event: any) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const optionsArray = useMemo(() => {
    return enabledOptions.length
      ? (Object.keys(options) as ContextMenuOptionType[]).filter((option) => enabledOptions.includes(option))
      : (Object.keys(options) as ContextMenuOptionType[]);
  }, [options, enabledOptions]);
  // const hasAvailableOptions: boolean = !!Object.values(props).find(Boolean);

  const hasAvailableOptions: boolean = useMemo(() => !!Object.values(options).find(Boolean), [options]);
  // type PropsType = { [key: string]: any };

  const selectDateObj = useCallback(
    (key: ContextMenuOptionType) => {
      const handler: ContextMenuOptionValue | undefined = options[key];
      if (typeof handler !== "function") {
        setGetDateObj(handler as ContextMenuOptionObjectType);
      }
    },
    [options]
  );

  const onSaveClick = useCallback(
    async (date: string) => {
      try {
        if (getDateObj) {
          await getDateObj.action(date);
          handleClose();
          setIsMenuOpen(false);
          externalOnClose?.();
        }
      } catch (error) {
        throw error;
      }
    },
    [getDateObj, externalOnClose]
  );

  const onCancelClick = useCallback(() => {
    handleClose();
    setIsMenuOpen(false);
    externalOnClose?.();
  }, [externalOnClose]);

  useEffect(() => {
    if (!!anchorEl && !isMenuOpen) {
      handleClose();
    }
  }, [isMenuOpen, anchorEl]);

  type hasLineAfterType = (option: ContextMenuOptionType) => boolean;
  const hasLineAfter: hasLineAfterType = useCallback(
    (option: ContextMenuOptionType): boolean => {
      if (typeof pasteLineAfter === "string") {
        return pasteLineAfter === option;
      } else {
        return pasteLineAfter.includes(option);
      }
    },
    [pasteLineAfter]
  );

  const triggerIcon: JSX.Element = useMemo(
    () => <div className="context-menu__trigger">{trigger}</div>,
    [trigger, dataTestid]
  );

  if (isHidden || !hasAvailableOptions) return <></>;

  return (
    <div className={classnames("context-menu", { [className]: !!className })}>
      <Popup
        overlayStyle={{ boxShadow: "0px 0px 6px rgba(0, 0, 0, 0.6)" }}
        contentStyle={{ zIndex: 1400 }}
        trigger={
          <IconButton
            size="sm"
            sx={{
              backgroundColor: isMenuOpen ? "rgba(0, 0, 0, 0.04)" : "transparent"
            }}
          >
            {tooltipMessage.length ? (
              <TooltipDiv tooltipMessage={tooltipMessage} offset={{ right: 15 }}>
                {triggerIcon}
              </TooltipDiv>
            ) : (
              triggerIcon
            )}
          </IconButton>
        }
        position={position}
        keepTooltipInside
        {...props}
        open={isMenuOpen}
        onOpen={() => {
          setIsMenuOpen(true);
          externalOnOpen?.();
        }}
        onClose={() => {
          setIsMenuOpen(false);
          externalOnClose?.();
        }}
        nested={!!anchorEl}
        arrow={false}
      >
        <div data-testid="context-menu" className="context-menu__body">
          {optionsArray.map((key: ContextMenuOptionType, index) => {
            if (!options[key]) return;
            const Component: CustomComponent<IOptionProps> = OptionsHash[key];
            if (Component) {
              const handler: ContextMenuOptionValue = options[key]!;
              const isPropsFunc: boolean = typeof handler === "function";
              const hasLine: boolean = hasLineAfter(key);
              return (
                <Component
                  theme={theme}
                  key={index}
                  hasLineAfter={hasLine}
                  className={!!anchorEl && key === "onConvertToPeriodic" ? "option--selected" : ""}
                  title={isPropsFunc ? undefined : (handler as ContextMenuOptionObjectType)?.title}
                  onClick={async (event?: any) => {
                    if (isPropsFunc) {
                      await (handler as ContextMenuOptionFunctionType)(event);
                      setIsMenuOpen(false);
                      externalOnClose?.();
                      return;
                    }
                    if ((handler as ContextMenuOptionObjectType)?.withDatePicker) {
                      handleOpen(event);
                      selectDateObj(key);
                      return;
                    }
                    await (options[key] as ContextMenuOptionObjectType)?.action?.(event);
                    setIsMenuOpen(false);
                    externalOnClose?.();
                  }}
                />
              );
            }
            return;
          })}
          <DatePickerStyled
            onCancelClick={onCancelClick}
            onSave={onSaveClick}
            anchorEl={anchorEl!}
            placement="right"
            offset={[0, 15]}
          />
        </div>
      </Popup>
    </div>
  );
};
